import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiX, HiCheck } from 'react-icons/hi';
import { FollowerItem, FollowingItem, FollowerUser } from '../../../types/profile.types';
import ChannelInitialIcon from '../../ui/ChannelInitialIcon/ChannelInitialIcon';
import { generateProfileUrl } from '../../../utils/urlSlug';
import Services from '../../../services/Services';
import { useAnimatedVisibility } from '../../../hooks/useAnimatedVisibility';
import styles from './FollowersFollowingsModal.module.css';

// Color palette for avatar backgrounds (alphabetically consistent)
const AVATAR_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#EAB308', // Yellow
  '#84CC16', // Lime
  '#22C55E', // Green
  '#10B981', // Emerald
  '#14B8A6', // Teal
  '#06B6D4', // Cyan
  '#0EA5E9', // Sky
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#D946EF', // Fuchsia
  '#EC4899', // Pink
  '#F43F5E', // Rose
  '#64748B', // Slate
];

// Get color for a name alphabetically
const getColorForName = (name: string): string => {
  if (!name || name.length === 0) {
    return AVATAR_COLORS[0];
  }
  const firstLetter = name.charAt(0).toUpperCase();
  const charCode = firstLetter.charCodeAt(0);
  // Map A-Z (65-90) to color index
  const index = (charCode - 65) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

type TabType = 'followers' | 'following';

interface FollowersFollowingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: TabType;
  token: string;
  userID?: string;
  currentUserID?: string;
  followerCount: number;
  followingCount: number;
  getTranslation: (key: string) => string;
}

const FollowersFollowingsModal: React.FC<FollowersFollowingsModalProps> = ({
  isOpen,
  onClose,
  initialTab = 'followers',
  token,
  userID,
  currentUserID,
  followerCount,
  followingCount,
  getTranslation
}) => {
  const navigate = useNavigate();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { shouldRender, isClosing, onAnimationEnd } = useAnimatedVisibility({
    isOpen,
    closeDuration: 300,
  });
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [followers, setFollowers] = useState<FollowerItem[]>([]);
  const [followings, setFollowings] = useState<FollowingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [followStatus, setFollowStatus] = useState<Record<string, boolean>>({});
  const [followLoading, setFollowLoading] = useState<Record<string, boolean>>({});

  // Check follow status for a list of users
  const checkFollowStatuses = useCallback(async (users: FollowerUser[]) => {
    if (!token || !currentUserID) return;

    const statusPromises = users
      .filter(user => {
        const userId = user.id || user.userID;
        return userId && userId !== currentUserID;
      })
      .map(async (user) => {
        const userId = user.id || user.userID;
        if (!userId) return null;
        try {
          const response = await Services.SocialHiveAmIFollowingUser(token, userId);
          return { userId, isFollowing: response.data.status && response.data.result };
        } catch {
          return { userId, isFollowing: false };
        }
      });

    const results = await Promise.all(statusPromises);
    const newStatus: Record<string, boolean> = {};
    results.forEach(result => {
      if (result) {
        newStatus[result.userId] = result.isFollowing;
      }
    });
    setFollowStatus(prev => ({ ...prev, ...newStatus }));
  }, [token, currentUserID]);

  // Fetch data when modal opens or tab changes
  const fetchData = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      if (activeTab === 'followers') {
        const response = await Services.SocialHiveGetMyFollowers(token, userID);
        if (response.data.status && response.data.result) {
          setFollowers(response.data.result);
          // Check follow status for all users
          const users = response.data.result.map(item => item.follower);
          checkFollowStatuses(users);
        }
      } else {
        const response = await Services.SocialHiveGetMyFollowings(token, userID);
        if (response.data.status && response.data.result) {
          setFollowings(response.data.result);
          // Check follow status for all users
          const users = response.data.result.map(item => item.following);
          checkFollowStatuses(users);
        }
      }
    } catch (err) {
      setError(getTranslation('common.error'));
    } finally {
      setLoading(false);
    }
  }, [token, userID, activeTab, getTranslation, checkFollowStatuses]);

  // Handle follow/unfollow action
  const handleFollowToggle = useCallback(async (e: React.MouseEvent, targetUserId: string) => {
    e.stopPropagation(); // Prevent navigation when clicking the button
    if (!token || !targetUserId || followLoading[targetUserId]) return;

    setFollowLoading(prev => ({ ...prev, [targetUserId]: true }));

    try {
      const isCurrentlyFollowing = followStatus[targetUserId];
      if (isCurrentlyFollowing) {
        await Services.SocialHiveUnfollowUser(token, targetUserId);
        setFollowStatus(prev => ({ ...prev, [targetUserId]: false }));
      } else {
        await Services.SocialHiveFollowUser(token, targetUserId);
        setFollowStatus(prev => ({ ...prev, [targetUserId]: true }));
      }
    } catch (err) {
      // Silently fail - could add toast notification here
    } finally {
      setFollowLoading(prev => ({ ...prev, [targetUserId]: false }));
    }
  }, [token, followStatus, followLoading]);

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      fetchData();
    }
  }, [isOpen, initialTab]);

  // Refetch when tab changes
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [activeTab, fetchData, isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Focus trap for accessibility
  useEffect(() => {
    if (!isOpen || !modalContentRef.current) return;

    const modal = modalContentRef.current;
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    firstElement?.focus();

    return () => {
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen]);

  // Get display name for a user
  const getDisplayName = (user: FollowerUser): string => {
    return `${user.name || ''} ${user.lastname || ''}`.trim() || 'Unknown User';
  };

  // Get profile photo URL
  const getProfilePhoto = (user: FollowerUser): string | null => {
    return user.userAvatar || user.avatar || user.image || null;
  };

  // Handle user click - close modal and navigate to profile
  const handleUserClick = (user: FollowerUser) => {
    const userId = user.id || user.userID;
    if (userId) {
      onClose();
      navigate(generateProfileUrl(user.name, user.lastname, userId));
    }
  };

  // Render user item
  const renderUserItem = (user: FollowerUser, index: number) => {
    const photoUrl = getProfilePhoto(user);
    const displayName = getDisplayName(user);
    const userId = user.id || user.userID;
    const isSelf = userId === currentUserID;
    const isFollowing = userId ? followStatus[userId] : false;
    const isButtonLoading = userId ? followLoading[userId] : false;

    return (
      <div
        key={`${userId}-${index}`}
        className={styles.userItem}
        onClick={() => handleUserClick(user)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleUserClick(user)}
      >
        <div className={styles.avatarWrapper}>
          <div className={styles.userAvatar}>
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={displayName}
                className={styles.avatarImage}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    const fallback = parent.querySelector(`.${styles.avatarFallback}`);
                    if (fallback) {
                      (fallback as HTMLElement).style.display = 'flex';
                    }
                  }
                }}
              />
            ) : null}
            <div
              className={styles.avatarFallback}
              style={{
                display: photoUrl ? 'none' : 'flex',
                backgroundColor: getColorForName(displayName),
                color: '#ffffff'
              }}
            >
              <ChannelInitialIcon
                channelName={displayName}
                size={48}
              />
            </div>
          </div>
        </div>
        <div className={styles.userInfo}>
          <p className={styles.userName}>{displayName}</p>
        </div>
        {!isSelf && userId && (
          <button
            className={`${styles.followButton} ${isFollowing ? styles.followButtonFollowing : ''}`}
            onClick={(e) => handleFollowToggle(e, userId)}
            disabled={isButtonLoading}
          >
            {isButtonLoading ? (
              <span className={styles.buttonSpinner} />
            ) : isFollowing ? (
              <>
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiCheck className={styles.followIcon} />
                {getTranslation('profile.following')}
              </>
            ) : (
              getTranslation('profile.follow')
            )}
          </button>
        )}
      </div>
    );
  };

  if (!shouldRender) return null;

  const currentList = activeTab === 'followers'
    ? followers.map(item => item.follower)
    : followings.map(item => item.following);

  return (
    <div
      className={`${styles.modalOverlay} ${isClosing ? styles.modalOverlayClosing : ''}`}
      onClick={onClose}
      onAnimationEnd={onAnimationEnd}
      role="dialog"
      aria-modal="true"
      aria-labelledby="followers-following-modal-title"
    >
      <div
        className={`${styles.modalContent} ${isClosing ? styles.modalContentClosing : ''}`}
        onClick={(e) => e.stopPropagation()}
        ref={modalContentRef}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle} id="followers-following-modal-title">
            {activeTab === 'followers'
              ? getTranslation('profile.followers')
              : getTranslation('profile.following')
            }
          </h3>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label={getTranslation('common.close')}
          >
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiX className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.tabContainer}>
          <button
            className={`${styles.tabButton} ${activeTab === 'followers' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('followers')}
          >
            {getTranslation('profile.followers')} ({followerCount})
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'following' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('following')}
          >
            {getTranslation('profile.following')} ({followingCount})
          </button>
        </div>

        <div className={styles.userList}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
            </div>
          ) : error ? (
            <div className={styles.emptyState}>
              <p>{error}</p>
            </div>
          ) : currentList.length === 0 ? (
            <div className={styles.emptyState}>
              <p>
                {activeTab === 'followers'
                  ? getTranslation('profile.noFollowers')
                  : getTranslation('profile.noFollowings')
                }
              </p>
            </div>
          ) : (
            currentList.map((user, index) => renderUserItem(user, index))
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowersFollowingsModal;
